// ============================================
// Cloud-sync state — app-local (English)
// ============================================
//
// JLPT kept `currentUser` / `syncStatus` inside its monolithic
// `src/state/signals.ts`. English's SRS signals come from lang-core
// (`createSrsSignals`), which has no opinion on auth/sync — so these
// cloud-sync-only signals live here, app-local.
//
// Also home to the **push-suppression** mechanism that replaces JLPT's
// StorageService post-flush-success hook. lang-core's StorageService exposes
// `setFlushErrorHandler` but NOT a post-flush *success* hook, so SyncService
// drives cloud pushes from a `@preact/signals` effect on `signals.wordProgress`
// (see SyncService.ts). That effect must fire ONLY on genuine answers
// (`signals.updateWordSRS`), never on programmatic whole-map replacements.
// Callers wrap those replacements in `withSuppressedPush(...)`; the effect
// skips while `isPushSuppressed()` is true. Currently wrapped: login-sync
// merges (SyncService.writeMergedLocal), level-load hydration + cross-tab
// rehydrate (main.tsx).
//
// ⚠️ NOT auto-covered: `signals.resetProgress()` calls `setWordProgressMap`
// INTERNALLY inside lang-core, which the app cannot wrap. English does not wire
// a reset button today, so there is no active push from reset. If one is ever
// added, wrap the call site in `withSuppressedPush(() => signals.resetProgress())`
// — otherwise reset would push an all-`new` map (harmless on merge since the
// merge is non-reducing, but a wasted write that bumps map_version).

import { signal } from '@preact/signals';
import { signals } from '../srs.js';

/** Supabase user id when signed in, null when anonymous. */
export const currentUser = signal<string | null>(null);

/** Last sync outcome — surfaced by AccountMenu's status chip. */
export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';
export const syncStatus = signal<SyncStatus>('idle');

// ── Push suppression ────────────────────────────────────────────────────────
// A plain module boolean (NOT a signal) so reading it inside the push effect
// doesn't create a tracked dependency. Re-entrant-safe via save/restore.
let _suppressDepth = 0;

/** True while a programmatic map replacement is in progress. */
export function isPushSuppressed(): boolean {
  return _suppressDepth > 0;
}

/**
 * Run `fn` with cloud-push suppressed. Used to wrap every whole-map write that
 * is NOT a user answer: login-sync merges, level-switch hydration, cross-tab
 * rehydrate, and reset. Preact effects triggered by a `batch()` inside `fn`
 * run synchronously before `fn` returns, so the suppression covers them.
 */
export function withSuppressedPush<T>(fn: () => T): T {
  _suppressDepth++;
  try {
    return fn();
  } finally {
    _suppressDepth--;
  }
}

// ── Actionable sync error toast ─────────────────────────────────────────────
// One-shot per session so a flapping auth error doesn't spam toasts. lang-core's
// `signals.resetAppState()` can't see this app-local flag, so tests reset it via
// `resetSyncSignals()` in beforeEach.
let actionableNotified = false;

/** Surface a user-fixable sync failure (expired session / RLS denial). */
export function notifySyncActionable(
  message = 'Session expired — sign in to resume sync'
): void {
  if (actionableNotified) return;
  actionableNotified = true;
  console.warn('[Sync] actionable failure:', message);
  signals.showNotification(message, 'error', 6000);
}

/** Test-only: restore cloud-sync state + one-shot flags to boot defaults. */
export function resetSyncSignals(): void {
  currentUser.value = null;
  syncStatus.value = 'idle';
  _suppressDepth = 0;
  actionableNotified = false;
}
